import React, { useState, useEffect } from 'react';
import {
  ShieldCheck,
  ArrowRight,
  Zap,
  Smartphone,
  Megaphone,
  Users,
  FileCheck,
  Lock,
  Check,
  X,
  AlertCircle,
  Layout,
  CreditCard,
  Settings,
  Layers,
} from 'lucide-react';
import { Navbar } from './components/Navbar';
import { StatsBar } from './components/StatsBar';
import { UserTable } from './components/UserTable';
import { SignaturePreview } from './components/SignaturePreview';
import { SignatureEditor } from './components/SignatureEditor';
import { GroupsManager } from './components/GroupsManager';
import { PricingSection } from './components/PricingSection';
import { SettingsTab } from './components/SettingsTab';
import { BatchModal } from './components/BatchModal';
import { SecurityModal } from './components/SecurityModal';
import { ContactModal } from './components/ContactModal';
import { ViralWelcomeBanner } from './components/ViralWelcomeBanner';
import { WorkspaceUser, AuthUser, BatchUpdateResponse, PlanType, SavedSignatureTemplate, UserGroup } from './types';
import {
  fetchWorkspaceUsers,
  injectSignaturesBatch,
  injectMySignature,
  fetchOrganizationPlan,
} from './services/api';
import {
  auth,
  loginWithGoogle,
  logoutUser,
  onAuthStateChanged,
  getStoredGoogleAccessToken,
} from './services/firebase';
import {
  getSavedTemplates,
  getUserGroups,
  syncUserGroupsWithFirestore,
  saveUserGroup,
  saveTemplate,
} from './services/templateGroupService';
import {
  batchAssignUsersSignatureInFirestore,
  assignUserSignatureInFirestore,
  batchAssignUsersGroupInFirestore,
  fetchAssignedUsersFromFirestore,
} from './services/signaturePersistenceService';

type DashboardTab = 'editor' | 'users' | 'groups' | 'pricing' | 'settings';

export const App: React.FC = () => {
  const [authUser, setAuthUser] = useState<AuthUser | null>(null);
  const [domain, setDomain] = useState('cerebrolabs.io');
  const [users, setUsers] = useState<WorkspaceUser[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [activePreviewUser, setActivePreviewUser] = useState<WorkspaceUser | null>(null);
  const currentPreviewUser = users.find((u) => u.id === activePreviewUser?.id) || activePreviewUser || users[0] || null;
  const [isLoading, setIsLoading] = useState(false);
  const [isLiveConnected, setIsLiveConnected] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  // Contact Modal State (Resend integration)
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [contactModalPlan, setContactModalPlan] = useState<string>('team10');

  const handleOpenContactModal = (planId: string = 'team10') => {
    setContactModalPlan(planId);
    setIsContactModalOpen(true);
  };

  // Active Dashboard Tab
  const [activeTab, setActiveTab] = useState<DashboardTab>('editor');

  // Organization Plan & OAuth Access Token
  const [currentPlan, setCurrentPlan] = useState<PlanType>('free');
  const [googleAccessToken, setGoogleAccessToken] = useState<string | null>(() => getStoredGoogleAccessToken());

  // Saved Signature Templates & Departmental User Groups
  const [savedTemplates, setSavedTemplates] = useState<SavedSignatureTemplate[]>(() => getSavedTemplates(domain));
  const [userGroups, setUserGroups] = useState<UserGroup[]>(() => getUserGroups(domain));

  // Reload groups and templates when domain changes, and sync with Firestore
  useEffect(() => {
    setSavedTemplates(getSavedTemplates(domain));
    setUserGroups(getUserGroups(domain));

    syncUserGroupsWithFirestore(domain).then((synced) => {
      if (synced && synced.length > 0) {
        setUserGroups(synced);
      }
    });
  }, [domain]);

  // Modals state
  const [isSecurityModalOpen, setIsSecurityModalOpen] = useState(false);
  const [isBatchModalOpen, setIsBatchModalOpen] = useState(false);
  const [batchProgress, setBatchProgress] = useState(0);
  const [isBatchProcessing, setIsBatchProcessing] = useState(false);
  const [batchResult, setBatchResult] = useState<BatchUpdateResponse | null>(null);
  const [isInjectingSingle, setIsInjectingSingle] = useState(false);

  // Monitor Firebase auth state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        setAuthUser({
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL,
        });
        if (user.email && user.email.includes('@')) {
          const userDomain = user.email.split('@')[1];
          setDomain(userDomain);
        }
        setAuthError(null);
        // Also refresh stored Google access token from session
        const storedToken = getStoredGoogleAccessToken();
        if (storedToken) {
          setGoogleAccessToken(storedToken);
        }
      } else {
        setAuthUser(null);
        setGoogleAccessToken(null);
      }
    });
    return () => unsubscribe();
  }, []);

  // Load organization plan and users when domain or authUser changes
  useEffect(() => {
    if (authUser) {
      loadUsers();
      loadPlan();
    }
  }, [authUser, domain]);

  const loadPlan = async () => {
    try {
      const planData = await fetchOrganizationPlan(domain);
      if (planData?.plan) {
        setCurrentPlan(planData.plan as PlanType);
      }
    } catch (e) {
      console.warn('Could not load plan:', e);
    }
  };

  const loadUsers = async () => {
    setIsLoading(true);
    try {
      const data = await fetchWorkspaceUsers(null, domain);
      let loadedUsers = data.users || [];

      // When logged in with a real Google account, ensure the real user is presented
      if (authUser?.email) {
        const userEmailLower = authUser.email.toLowerCase();
        const existingIndex = loadedUsers.findIndex(
          (u) => u.primaryEmail.toLowerCase() === userEmailLower
        );

        if (existingIndex >= 0) {
          loadedUsers[existingIndex] = {
            ...loadedUsers[existingIndex],
            fullName: authUser.displayName || loadedUsers[existingIndex].fullName,
            avatarUrl: authUser.photoURL || loadedUsers[existingIndex].avatarUrl,
          };
        } else {
          const selfUser: WorkspaceUser = {
            id: authUser.uid,
            primaryEmail: authUser.email,
            fullName: authUser.displayName || authUser.email.split('@')[0],
            jobTitle: 'Administrator Google Workspace',
            department: 'Zarząd / IT',
            phone: '+48 600 000 000',
            avatarUrl: authUser.photoURL || undefined,
            signatureStatus: 'pending',
          };

          if (domain !== 'cerebrolabs.io') {
            loadedUsers = [selfUser];
          } else {
            loadedUsers = [selfUser, ...loadedUsers];
          }
        }
      }

      // Merge user assignments from Firestore (organizations/{domain}/users)
      try {
        const userOverrides = await fetchAssignedUsersFromFirestore(domain);
          loadedUsers = loadedUsers.map((u) => {
            const uid = u.id || u.primaryEmail;
            const override = (u.id ? userOverrides[u.id] : undefined) || userOverrides[u.primaryEmail] || userOverrides[uid];
            if (override) {
              return {
                ...u,
                id: uid,
                ...override,
                assignedSignatureId: override.assignedSignatureId || u.assignedSignatureId,
                customTemplateId: override.assignedSignatureId || u.customTemplateId,
                groupId: override.groupId || u.groupId,
              };
            }
            return {
              ...u,
              id: uid,
            };
          });
      } catch (err) {
        console.warn('Nie udało się wczytać przypisań użytkowników z Firestore:', err);
      }

      setUsers(loadedUsers);
      setIsLiveConnected(data.isLiveConnected);

      if (loadedUsers.length > 0) {
        const targetPreview =
          loadedUsers.find((u) => u.primaryEmail.toLowerCase() === authUser?.email?.toLowerCase()) ||
          loadedUsers[0];
        setActivePreviewUser(targetPreview);

        const defaultSelected = new Set(
          loadedUsers.filter((u) => u.signatureStatus !== 'synced').map((u) => u.id)
        );
        setSelectedUserIds(defaultSelected.size > 0 ? defaultSelected : new Set([targetPreview.id]));
      }
    } catch (err) {
      console.error('Błąd ładowania użytkowników:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setIsLoggingIn(true);
    setAuthError(null);
    try {
      const { accessToken } = await loginWithGoogle();
      if (accessToken) {
        setGoogleAccessToken(accessToken);
      }
    } catch (error: any) {
      console.error('[Firebase Google Auth Error]:', error);
      if (error?.code === 'auth/popup-closed-by-user') {
        setAuthError('Okno logowania zostało zamknięte przed ukończeniem autoryzacji.');
      } else if (error?.code === 'auth/popup-blocked') {
        setAuthError('Przeglądarka zablokowała wyskakujące okienko Google. Zezwól na wyskakujące okienka (pop-upy) dla tej strony i spróbuj ponownie.');
      } else if (error?.code === 'auth/cancelled-popup-request') {
        // Ignored
      } else {
        setAuthError(error?.message || 'Wystąpił błąd podczas logowania przez konto Google.');
      }
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logoutUser();
    } catch (e) {
      console.warn('Logout error:', e);
    }
    setAuthUser(null);
    setGoogleAccessToken(null);
  };

  const handleToggleSelectUser = (userId: string) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const handleToggleSelectAll = () => {
    if (selectedUserIds.size === users.length) {
      setSelectedUserIds(new Set());
    } else {
      setSelectedUserIds(new Set(users.map((u) => u.id)));
    }
  };

  // Trigger batch injection into Gmail API
  const handleTriggerBatchInjection = async () => {
    const targetUsers = users.filter((u) => selectedUserIds.has(u.id));
    if (targetUsers.length === 0) return;

    let token = googleAccessToken || getStoredGoogleAccessToken();
    if (!token) {
      try {
        const res = await loginWithGoogle();
        token = res.accessToken;
        if (token) {
          setGoogleAccessToken(token);
        }
      } catch (authErr: any) {
        console.warn('Google sign-in skipped or cancelled:', authErr);
        alert('Do wstrzyknięcia stopek do Gmaila wymagana jest autoryzacja konta Google.');
        return;
      }
    }

    if (!token) {
      alert('Do wstrzyknięcia stopek do Gmaila wymagana jest autoryzacja konta Google.');
      return;
    }

    setIsBatchModalOpen(true);
    setIsBatchProcessing(true);
    setBatchProgress(10);
    setBatchResult(null);

    try {
      const progressTimer = setInterval(() => {
        setBatchProgress((p) => (p < 85 ? p + 15 : p));
      }, 300);

      let response: BatchUpdateResponse;
      try {
        response = await injectSignaturesBatch(targetUsers, null, domain, token, savedTemplates);
      } catch (batchErr: any) {
        if (
          batchErr.message?.includes('BRAK_TOKENU_GOOGLE') ||
          batchErr.message?.includes('autoryzacji') ||
          batchErr.message?.includes('401')
        ) {
          const res = await loginWithGoogle();
          token = res.accessToken;
          if (token) {
            setGoogleAccessToken(token);
            response = await injectSignaturesBatch(targetUsers, null, domain, token, savedTemplates);
          } else {
            throw batchErr;
          }
        } else {
          throw batchErr;
        }
      }

      clearInterval(progressTimer);
      setBatchProgress(100);
      setBatchResult(response);

      // Update local users status
      setUsers((prev) =>
        prev.map((u) => {
          if (selectedUserIds.has(u.id)) {
            return {
              ...u,
              signatureStatus: 'synced',
              lastSyncedAt: new Date().toISOString(),
            };
          }
          return u;
        })
      );
    } catch (err: any) {
      console.error('Batch error:', err);
      alert(err.message || 'Wystąpił błąd podczas masowego wstrzykiwania stopek.');
    } finally {
      setIsBatchProcessing(false);
    }
  };

  // Single injection
  const handleInjectSingle = async (user: WorkspaceUser, signatureHtml: string) => {
    setIsInjectingSingle(true);
    try {
      let token = googleAccessToken || getStoredGoogleAccessToken();
      if (!token) {
        const res = await loginWithGoogle();
        token = res.accessToken;
        if (token) {
          setGoogleAccessToken(token);
        }
      }

      if (!token) {
        throw new Error('Brak uprawnień Google do Gmaila. Zaloguj się ponownie.');
      }

      try {
        await injectMySignature({
          googleAccessToken: token,
          domain,
          userEmail: user.primaryEmail,
          signatureHtml,
        });
      } catch (singleErr: any) {
        if (
          singleErr.message?.includes('BRAK_TOKENU_GOOGLE') ||
          singleErr.message?.includes('autoryzacji') ||
          singleErr.message?.includes('401')
        ) {
          const res = await loginWithGoogle();
          token = res.accessToken;
          if (token) {
            setGoogleAccessToken(token);
            await injectMySignature({
              googleAccessToken: token,
              domain,
              userEmail: user.primaryEmail,
              signatureHtml,
            });
          } else {
            throw singleErr;
          }
        } else {
          throw singleErr;
        }
      }

      const nowIso = new Date().toISOString();
      setUsers((prev) =>
        prev.map((u) =>
          u.id === user.id
            ? { ...u, signatureStatus: 'synced', lastSyncedAt: nowIso }
            : u
        )
      );
      setActivePreviewUser((prev) =>
        prev?.id === user.id
          ? { ...prev, signatureStatus: 'synced', lastSyncedAt: nowIso }
          : prev
      );
      alert(`Stopka została pomyślnie wstrzyknięta do Gmaila dla konta ${user.primaryEmail}!`);
    } catch (err: any) {
      console.error('Single injection error:', err);
      alert(err.message || 'Nie udało się wstrzyknąć stopki');
    } finally {
      setIsInjectingSingle(false);
    }
  };

  // Group & Template management handlers
  const handleSaveTemplate = (tpl: SavedSignatureTemplate) => {
    saveTemplate(domain, tpl);
    setSavedTemplates((prev) => {
      const filtered = prev.filter((t) => t.id !== tpl.id);
      return [tpl, ...filtered];
    });
  };

  const handleAssignGroup = async (userIds: string[], groupId: string) => {
    const targetGroup = userGroups.find((g) => g.id === groupId);
    const targetTemplateId = targetGroup?.savedTemplateId;
    const targetUsersList: WorkspaceUser[] = [];

    setUsers((prev) =>
      prev.map((u) => {
        const matches = userIds.includes(u.id) || (u.primaryEmail && userIds.includes(u.primaryEmail));
        if (matches) {
          targetUsersList.push(u);
          const updated = { ...u, groupId };
          if (targetTemplateId) {
            updated.assignedSignatureId = targetTemplateId;
            updated.customTemplateId = targetTemplateId;
          }
          return updated;
        }
        return u;
      })
    );
    setActivePreviewUser((prev) => {
      if (!prev) return prev;
      const matches = userIds.includes(prev.id) || (prev.primaryEmail && userIds.includes(prev.primaryEmail));
      if (matches) {
        const updated = { ...prev, groupId };
        if (targetTemplateId) {
          updated.assignedSignatureId = targetTemplateId;
          updated.customTemplateId = targetTemplateId;
        }
        return updated;
      }
      return prev;
    });

    try {
      const usersToPersist = targetUsersList.length > 0
        ? targetUsersList
        : userIds.map((id) => ({ id, primaryEmail: id.includes('@') ? id : '' }));
      await batchAssignUsersGroupInFirestore(domain, usersToPersist, groupId, targetTemplateId);
    } catch (e) {
      console.error('[Firestore Error] Błąd zapisu przypisania grupy do bazy Firestore:', e);
    }
  };

  const handleAssignSignature = async (userIds: string[], signatureId: string) => {
    try {
      const targetUsersList = users.filter((u) => userIds.includes(u.id) || (u.primaryEmail && userIds.includes(u.primaryEmail)));
      if (targetUsersList.length > 0) {
        await Promise.all(
          targetUsersList.map((u) => assignUserSignatureInFirestore(domain, u.id, signatureId, u.primaryEmail))
        );
      } else {
        await batchAssignUsersSignatureInFirestore(domain, userIds, signatureId);
      }
      setUsers((prev) =>
        prev.map((u) => {
          const matches = userIds.includes(u.id) || (u.primaryEmail && userIds.includes(u.primaryEmail));
          return matches
            ? { ...u, assignedSignatureId: signatureId, customTemplateId: signatureId }
            : u;
        })
      );
      setActivePreviewUser((prev) => {
        if (!prev) return prev;
        const matches = userIds.includes(prev.id) || (prev.primaryEmail && userIds.includes(prev.primaryEmail));
        return matches
          ? { ...prev, assignedSignatureId: signatureId, customTemplateId: signatureId }
          : prev;
      });
    } catch (e) {
      console.error('Błąd zapisu przypisania szablonu do użytkowników w Firestore:', e);
    }
  };

  const handleApplyTemplateToGroup = async (groupId: string, templateId: string) => {
    try {
      // 1. Persist group template update to localStorage AND Firestore
      const targetGroup = userGroups.find((g) => g.id === groupId);
      if (targetGroup) {
        saveUserGroup(domain, { ...targetGroup, savedTemplateId: templateId });
      }

      setUserGroups((prev) =>
        prev.map((g) => (g.id === groupId ? { ...g, savedTemplateId: templateId } : g))
      );

      // 2. Identify all users in this group (including Zarząd / Dyrekcja role matches)
      const groupUserIds = users
        .filter((u) => {
          const uGrp = u.groupId || 'group_main';
          if (uGrp === groupId) return true;
          if (
            groupId === 'group_executive' &&
            (u.department?.toLowerCase().includes('zarząd') ||
              u.department?.toLowerCase().includes('dyrekcja') ||
              u.jobTitle?.toLowerCase().includes('prezes') ||
              u.jobTitle?.toLowerCase().includes('dyrektor'))
          ) {
            return true;
          }
          return false;
        })
        .map((u) => u.id);

      if (groupUserIds.length > 0) {
        await batchAssignUsersSignatureInFirestore(domain, groupUserIds, templateId);
      }

      setUsers((prev) =>
        prev.map((u) => {
          const matches =
            (u.groupId || 'group_main') === groupId ||
            (groupId === 'group_executive' &&
              (u.department?.toLowerCase().includes('zarząd') ||
                u.department?.toLowerCase().includes('dyrekcja') ||
                u.jobTitle?.toLowerCase().includes('prezes') ||
                u.jobTitle?.toLowerCase().includes('dyrektor')));
          return matches
            ? { ...u, groupId: groupId, customTemplateId: templateId, assignedSignatureId: templateId }
            : u;
        })
      );

      setActivePreviewUser((prev) => {
        if (!prev) return prev;
        const matches =
          (prev.groupId || 'group_main') === groupId ||
          (groupId === 'group_executive' &&
            (prev.department?.toLowerCase().includes('zarząd') ||
              prev.department?.toLowerCase().includes('dyrekcja') ||
              prev.jobTitle?.toLowerCase().includes('prezes') ||
              prev.jobTitle?.toLowerCase().includes('dyrektor')));
        return matches
          ? { ...prev, groupId: groupId, customTemplateId: templateId, assignedSignatureId: templateId }
          : prev;
      });
    } catch (e) {
      console.error('Błąd przypisywania szablonu do grupy w Firestore:', e);
    }
  };

  const handleApplyTemplateToUser = async (userId: string, templateId: string) => {
    try {
      await assignUserSignatureInFirestore(domain, userId, templateId);
      setUsers((prev) =>
        prev.map((u) =>
          u.id === userId
            ? { ...u, customTemplateId: templateId, assignedSignatureId: templateId }
            : u
        )
      );
      setActivePreviewUser((prev) =>
        prev && prev.id === userId
          ? { ...prev, customTemplateId: templateId, assignedSignatureId: templateId }
          : prev
      );
    } catch (e) {
      console.error('Błąd przypisywania szablonu do użytkownika w Firestore:', e);
    }
  };

  const handleApplyTemplateToAll = async (templateId: string) => {
    try {
      const allUserIds = users.map((u) => u.id);
      if (allUserIds.length > 0) {
        await batchAssignUsersSignatureInFirestore(domain, allUserIds, templateId);
      }
      setUsers((prev) =>
        prev.map((u) => ({
          ...u,
          customTemplateId: templateId,
          assignedSignatureId: templateId,
        }))
      );
      setActivePreviewUser((prev) =>
        prev
          ? { ...prev, customTemplateId: templateId, assignedSignatureId: templateId }
          : prev
      );
    } catch (e) {
      console.error('Błąd stosowania szablonu do wszystkich użytkowników:', e);
    }
  };

  const handleViralBannerCta = () => {
    if (!authUser) {
      handleGoogleLogin();
    } else {
      setActiveTab('editor');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <ViralWelcomeBanner onCtaClick={handleViralBannerCta} />
      <Navbar
        user={authUser}
        onLogout={handleLogout}
        onOpenSecurityModal={() => setIsSecurityModalOpen(true)}
        onOpenContactModal={() => handleOpenContactModal('general')}
        domain={domain}
        onLogin={handleGoogleLogin}
        onNavigatePricing={() => {
          if (!authUser) {
            const el = document.getElementById('pricing-section');
            if (el) el.scrollIntoView({ behavior: 'smooth' });
          } else {
            setActiveTab('pricing');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
        }}
      />

      <main className="app-container" style={{ flex: 1 }}>
        {!authUser ? (
          /* Enterprise B2B SaaS Google Workspace Landing Page */
          <div
            style={{
              maxWidth: 1180,
              margin: '36px auto 0',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 48,
            }}
          >
            {/* Hero Section */}
            <div
              style={{
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 22,
                maxWidth: 920,
              }}
            >
              {/* Top Enterprise Badge */}
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '6px 16px',
                  background: '#eff6ff',
                  border: '1px solid #bfdbfe',
                  borderRadius: 6,
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: '#1d4ed8',
                  fontFamily: 'var(--font-body)',
                }}
              >
                <ShieldCheck size={16} color="#0066FF" />
                <span>Oficjalna Integracja Google Workspace &bull; Bezpieczna Chmura</span>
                <span className="micro-tag micro-tag-info" style={{ marginLeft: 4 }}>Dla Firm</span>
              </div>

              {/* Main Headline - Plus Jakarta Sans, Deep Navy & Certified Safety Blue */}
              <h1
                style={{
                  fontSize: 'clamp(28px, 4.8vw, 50px)',
                  fontWeight: 800,
                  lineHeight: 1.15,
                  letterSpacing: '-0.03em',
                  color: '#0B192C',
                  fontFamily: 'var(--font-display)',
                  margin: 0,
                }}
              >
                Centralne zarządzanie i standaryzacja stopek e-mail w{' '}
                <span style={{ color: '#0066FF' }}>
                  Google Workspace
                </span>
              </h1>

              {/* Subheadline focused on corporate efficiency & security */}
              <p
                style={{
                  fontSize: 'clamp(15px, 1.6vw, 18px)',
                  color: '#475569',
                  lineHeight: 1.6,
                  maxWidth: 780,
                  margin: 0,
                  fontFamily: 'var(--font-body)',
                }}
              >
                Wyeliminuj błędy manualne i chaos wizerunkowy. CerebroSign automatycznie wdraża,
                aktualizuje i ujednolica podpisy dla każdego pracownika w Twojej domenie bezpośrednio
                w Gmailu — z gwarancją 100% prywatności i całkowitym brakiem dostępu do treści Twoich wiadomości e-mail.
              </p>

              {/* Definition Block */}
              <div
                role="region"
                aria-label="Definicja CerebroSign Workspace Edition"
                style={{
                  maxWidth: 860,
                  width: '100%',
                  background: '#ffffff',
                  border: '1px solid #e2e8f0',
                  borderRadius: 10,
                  padding: '18px 22px',
                  textAlign: 'left',
                  boxShadow: '0 4px 16px rgba(11, 25, 44, 0.04)',
                }}
              >
                <dl style={{ margin: 0 }}>
                  <dt
                    style={{
                      fontSize: 11.5,
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      color: '#0066FF',
                      marginBottom: 6,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <span className="micro-tag micro-tag-info">Definicja platformy</span>
                    <span style={{ color: '#0B192C' }}>CerebroSign Workspace Edition by CerebroLabs</span>
                  </dt>
                  <dd style={{ margin: 0, fontSize: 13.5, color: '#334155', lineHeight: 1.6 }}>
                    <strong>CerebroSign</strong> to bezpieczna platforma biznesowa służąca do centralnego zarządzania stopkami e-mail w firmowych kontach Google Workspace. Aplikacja posiada uprawnienia wyłącznie do edycji pola podpisu, co gwarantuje pełne bezpieczeństwo i brak wglądu w korespondencję pracowników. Umożliwia standaryzację profesjonalnych szablonów HTML poprawnie wyświetlanych na każdym urządzeniu i programie pocztowym, automatyczne pobieranie danych pracowników oraz prowadzenie kampanii banerowych.
                  </dd>
                </dl>
              </div>

              {/* Google Sign-in CTA Button & Security Verification */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14, marginTop: 4 }}>
                <button
                  onClick={handleGoogleLogin}
                  disabled={isLoggingIn}
                  className="btn btn-primary hero-cta-btn"
                  style={{
                    padding: '15px 34px',
                    fontSize: 15.5,
                    fontWeight: 700,
                    borderRadius: 8,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    cursor: isLoggingIn ? 'not-allowed' : 'pointer',
                  }}
                >
                  {/* Google Workspace "G" Icon */}
                  <svg width="20" height="20" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
                    <path
                      fill="#ffffff"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#ffffff"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#ffffff"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
                    />
                    <path
                      fill="#ffffff"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
                    />
                  </svg>
                  <span>{isLoggingIn ? 'Logowanie przez Google...' : 'Zaloguj się z Google Workspace'}</span>
                  <ArrowRight size={17} />
                </button>

                {authError && (
                  <div
                    style={{
                      maxWidth: 520,
                      padding: '12px 16px',
                      background: 'rgba(239, 68, 68, 0.12)',
                      border: '1px solid rgba(239, 68, 68, 0.35)',
                      borderRadius: 8,
                      color: '#dc2626',
                      fontSize: 13,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      textAlign: 'left',
                    }}
                  >
                    <AlertCircle size={18} style={{ flexShrink: 0 }} />
                    <span>{authError}</span>
                  </div>
                )}

                {/* Trust Micro-Tags */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 14,
                    fontSize: 12,
                    color: '#64748b',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                  }}
                >
                  <span className="micro-tag micro-tag-success">
                    <ShieldCheck size={12} />
                    Gwarancja Prywatności
                  </span>
                  <span className="micro-tag micro-tag-info">
                    <Lock size={12} />
                    Brak wglądu w Twoją pocztę
                  </span>
                  <span className="micro-tag micro-tag-neutral">
                    Zgodność z RODO
                  </span>
                </div>
              </div>
            </div>

            {/* Architecture Security Blocks */}
            <section
              aria-label="Certyfikowana Architektura Bezpieczeństwa"
              style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 16 }}
            >
              <div style={{ textAlign: 'center', marginBottom: 4 }}>
                <span className="micro-tag micro-tag-info" style={{ marginBottom: 6 }}>
                  Standard Bezpieczeństwa
                </span>
                <h2
                  style={{
                    fontSize: 22,
                    fontWeight: 700,
                    color: '#0B192C',
                    fontFamily: 'var(--font-display)',
                    margin: '4px 0',
                  }}
                >
                  100% Prywatności i Bezpieczeństwa Twojej Poczty
                </h2>
                <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>
                  Aplikacja została zaprojektowana zgodnie z wytycznymi bezpieczeństwa Google Cloud Platform.
                </p>
              </div>

              <div className="security-blocks-grid">
                <article className="security-block-card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span className="micro-tag micro-tag-success">Autoryzacja Google</span>
                    <ShieldCheck size={18} color="#059669" />
                  </div>
                  <h3 style={{ fontSize: 15, fontWeight: 700, color: '#0B192C', margin: 0, fontFamily: 'var(--font-display)' }}>
                    Dostęp wyłącznie do pola podpisu
                  </h3>
                  <p style={{ fontSize: 12.5, color: '#475569', margin: 0, lineHeight: 1.55 }}>
                    CerebroSign modyfikuje wyłącznie pole stopki w ustawieniach konta. Aplikacja nie posiada uprawnień do odczytywania wiadomości, przeglądania skrzynek odbiorczych ani wysyłania maili w imieniu pracowników.
                  </p>
                </article>

                <article className="security-block-card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span className="micro-tag micro-tag-info">Poufność Firmowa</span>
                    <Lock size={18} color="#0066FF" />
                  </div>
                  <h3 style={{ fontSize: 15, fontWeight: 700, color: '#0B192C', margin: 0, fontFamily: 'var(--font-display)' }}>
                    Całkowity brak wglądu w maile
                  </h3>
                  <p style={{ fontSize: 12.5, color: '#475569', margin: 0, lineHeight: 1.55 }}>
                    Nasz system nie czyta, nie przetwarza ani nie archiwizuje wiadomości e-mail Twojej firmy. Korespondencja pracowników pozostaje w 100% poufna i bezpieczna w chmurze Google.
                  </p>
                </article>

                <article className="security-block-card">
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span className="micro-tag micro-tag-neutral">Zgodność Prawna</span>
                    <FileCheck size={18} color="#64748b" />
                  </div>
                  <h3 style={{ fontSize: 15, fontWeight: 700, color: '#0B192C', margin: 0, fontFamily: 'var(--font-display)' }}>
                    Zgodność z RODO i szyfrowanie SSL
                  </h3>
                  <p style={{ fontSize: 12.5, color: '#475569', margin: 0, lineHeight: 1.55 }}>
                    Umożliwia wdrożenie standaryzowanych not prawnych, danych rejestrowych (NIP, KRS) i klauzul poufności. Komunikacja szyfrowana protokołem TLS 1.3 z bezpiecznym logowaniem Google.
                  </p>
                </article>
              </div>
            </section>

            {/* Value Proposition Cards - Enterprise Grid */}
            <section
              aria-label="Kluczowe możliwości CerebroSign"
              style={{
                width: '100%',
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                gap: 18,
              }}
            >
              <article className="card card-enterprise" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 8,
                      background: '#eff6ff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#0066FF',
                    }}
                  >
                    <Zap size={20} />
                  </div>
                  <span className="micro-tag micro-tag-info">Automatyzacja</span>
                </div>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: '#0B192C', margin: 0, fontFamily: 'var(--font-display)' }}>
                  Bezpośrednie wdrożenie do Gmaila
                </h3>
                <p style={{ fontSize: 13, color: '#475569', lineHeight: 1.55, margin: 0 }}>
                  Automatyczna synchronizacja przez certyfikowaną integrację Google. Nowe podpisy pojawiają się w skrzynkach pracowników natychmiast bez angażowania użytkowników.
                </p>
              </article>

              <article className="card card-enterprise" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 8,
                      background: '#ecfdf5',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#059669',
                    }}
                  >
                    <Smartphone size={20} />
                  </div>
                  <span className="micro-tag micro-tag-success">Każde urządzenie</span>
                </div>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: '#0B192C', margin: 0, fontFamily: 'var(--font-display)' }}>
                  Szablony odporne na Outlook i smartfony
                </h3>
                <p style={{ fontSize: 13, color: '#475569', lineHeight: 1.55, margin: 0 }}>
                  Projektowane na bazie tabelarycznego kodu HTML. Wyglądają nienagannie na telefonach iPhone i Android oraz w programach Outlook i Apple Mail.
                </p>
              </article>

              <article className="card card-enterprise" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div
                    style={{
                      width: 36,
                      height: 36,
                      borderRadius: 8,
                      background: '#eff6ff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#0066FF',
                    }}
                  >
                    <Megaphone size={20} />
                  </div>
                  <span className="micro-tag micro-tag-info">Marketing i Sprzedaż</span>
                </div>
                <h3 style={{ fontSize: 16, fontWeight: 700, color: '#0B192C', margin: 0, fontFamily: 'var(--font-display)' }}>
                  Centralne banery promocyjne i linki
                </h3>
                <p style={{ fontSize: 13, color: '#475569', lineHeight: 1.55, margin: 0 }}>
                  Zamień firmowe maile w skuteczny kanał informacyjny. Dodawaj klikalne banery reklamowe z linkami i automatycznymi parametrami analitycznymi.
                </p>
              </article>
            </section>

            {/* Before vs After Enterprise Comparison Matrix */}
            <section aria-labelledby="comparison-heading" style={{ width: '100%', marginTop: 6 }}>
              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <span className="micro-tag micro-tag-info" style={{ marginBottom: 6 }}>
                  Efektywność Operacyjna
                </span>
                <h2
                  id="comparison-heading"
                  style={{
                    fontSize: 22,
                    fontWeight: 700,
                    color: '#0B192C',
                    fontFamily: 'var(--font-display)',
                    margin: '4px 0',
                  }}
                >
                  Przed vs. Po wdrożeniu CerebroSign
                </h2>
                <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>
                  Porównanie: ręczny chaos vs. automatyczne zarządzanie w firmie.
                </p>
              </div>

              <div className="enterprise-comparison-grid">
                {/* Header Row */}
                <div className="comparison-header-row">
                  <div className="comparison-header-col comparison-header-before">
                    <div>
                      <span className="micro-tag micro-tag-danger" style={{ marginBottom: 4 }}>
                        Tradycyjnie
                      </span>
                      <h3 style={{ fontSize: 16, fontWeight: 700, color: '#dc2626', margin: 0, fontFamily: 'var(--font-display)' }}>
                        Brak centralizacji (Chaos i ryzyko)
                      </h3>
                    </div>
                    <X size={22} color="#dc2626" />
                  </div>

                  <div className="comparison-header-col comparison-header-after">
                    <div>
                      <span className="micro-tag micro-tag-success" style={{ marginBottom: 4 }}>
                        Z CerebroSign
                      </span>
                      <h3 style={{ fontSize: 16, fontWeight: 700, color: '#059669', margin: 0, fontFamily: 'var(--font-display)' }}>
                        Nowoczesny standard i pełna kontrola
                      </h3>
                    </div>
                    <Check size={22} color="#059669" />
                  </div>
                </div>

                {/* Row 1 */}
                <div className="comparison-pair-row">
                  <div className="comparison-item" style={{ background: '#ffffff', borderColor: '#fecaca' }}>
                    <span className="micro-tag micro-tag-danger">Błąd ludzki</span>
                    <div className="comparison-item-content">
                      <strong style={{ fontSize: 13, color: '#991b1b' }}>Ręczne wklejanie przez pracowników</strong>
                      <span style={{ fontSize: 12, color: '#64748b' }}>Pracownicy kopiują kod ze starych maili, gubiąc formatowanie, linki i firmowe czcionki.</span>
                    </div>
                  </div>

                  <div className="comparison-item" style={{ background: '#f0fdf4', borderColor: '#bbf7d0' }}>
                    <span className="micro-tag micro-tag-success">100% Automatyzacji</span>
                    <div className="comparison-item-content">
                      <strong style={{ fontSize: 13, color: '#0B192C' }}>Wdrożenie jednym kliknięciem z panelu</strong>
                      <span style={{ fontSize: 12, color: '#334155' }}>Podpisy wstrzykiwane bezpośrednio do Gmaila bez angażowania pracownika.</span>
                    </div>
                  </div>
                </div>

                {/* Row 2 */}
                <div className="comparison-pair-row">
                  <div className="comparison-item" style={{ background: '#ffffff', borderColor: '#fecaca' }}>
                    <span className="micro-tag micro-tag-danger">Chaos marki</span>
                    <div className="comparison-item-content">
                      <strong style={{ fontSize: 13, color: '#991b1b' }}>Brak spójności wizerunkowej</strong>
                      <span style={{ fontSize: 12, color: '#64748b' }}>Rozciągnięte logo, przypadkowe kolory i nieprofesjonalny wygląd przed klientami.</span>
                    </div>
                  </div>

                  <div className="comparison-item" style={{ background: '#f0fdf4', borderColor: '#bbf7d0' }}>
                    <span className="micro-tag micro-tag-success">Spójna Marka</span>
                    <div className="comparison-item-content">
                      <strong style={{ fontSize: 13, color: '#0B192C' }}>Nienaganny wizerunek i szablony tabelaryczne</strong>
                      <span style={{ fontSize: 12, color: '#334155' }}>100% poprawności wyświetlania w Outlooku, Apple Mail, na iPhone i Androidzie.</span>
                    </div>
                  </div>
                </div>

                {/* Row 3 */}
                <div className="comparison-pair-row">
                  <div className="comparison-item" style={{ background: '#ffffff', borderColor: '#fecaca' }}>
                    <span className="micro-tag micro-tag-danger">Ryzyko prawne</span>
                    <div className="comparison-item-content">
                      <strong style={{ fontSize: 13, color: '#991b1b' }}>Nieaktualne dane i brak klauzuli RODO</strong>
                      <span style={{ fontSize: 12, color: '#64748b' }}>Stare numery KRS, błędne telefony i brak obowiązkowych not prawnych w stopce.</span>
                    </div>
                  </div>

                  <div className="comparison-item" style={{ background: '#f0fdf4', borderColor: '#bbf7d0' }}>
                    <span className="micro-tag micro-tag-success">Zgodność RODO</span>
                    <div className="comparison-item-content">
                      <strong style={{ fontSize: 13, color: '#0B192C' }}>Automatyczna synchronizacja katalogu Google</strong>
                      <span style={{ fontSize: 12, color: '#334155' }}>Zawsze aktualne stanowiska, telefony i obowiązkowe formuły prawne per dział.</span>
                    </div>
                  </div>
                </div>

                {/* Row 4 */}
                <div className="comparison-pair-row">
                  <div className="comparison-item" style={{ background: '#ffffff', borderColor: '#fecaca' }}>
                    <span className="micro-tag micro-tag-danger">Obciążenie IT</span>
                    <div className="comparison-item-content">
                      <strong style={{ fontSize: 13, color: '#991b1b' }}>Godziny stracone na pomoc techniczną</strong>
                      <span style={{ fontSize: 12, color: '#64748b' }}>Administratorzy tworzą instrukcje w PDF i pomagają każdemu użytkownikowi osobno.</span>
                    </div>
                  </div>

                  <div className="comparison-item" style={{ background: '#f0fdf4', borderColor: '#bbf7d0' }}>
                    <span className="micro-tag micro-tag-success">Zero IT Helpdesk</span>
                    <div className="comparison-item-content">
                      <strong style={{ fontSize: 13, color: '#0B192C' }}>Oszczędność czasu administratorów IT</strong>
                      <span style={{ fontSize: 12, color: '#334155' }}>Centralna kontrola i masowa synchronizacja dla całego zespołu w kilka sekund.</span>
                    </div>
                  </div>
                </div>

                {/* Row 5 */}
                <div className="comparison-pair-row">
                  <div className="comparison-item" style={{ background: '#ffffff', borderColor: '#fecaca' }}>
                    <span className="micro-tag micro-tag-danger">Brak marketingu</span>
                    <div className="comparison-item-content">
                      <strong style={{ fontSize: 13, color: '#991b1b' }}>Brak możliwości rotacji banerów</strong>
                      <span style={{ fontSize: 12, color: '#64748b' }}>Brak możliwości szybkiego uruchomienia promocji lub wydarzenia w mailach całego zespołu.</span>
                    </div>
                  </div>

                  <div className="comparison-item" style={{ background: '#f0fdf4', borderColor: '#bbf7d0' }}>
                    <span className="micro-tag micro-tag-success">Kanał Sprzedaży</span>
                    <div className="comparison-item-content">
                      <strong style={{ fontSize: 13, color: '#0B192C' }}>Centralne banery promocyjne i linki</strong>
                      <span style={{ fontSize: 12, color: '#334155' }}>Błyskawiczne uruchamianie kampanii banerowych w stopkach z analityką kliknięć.</span>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            {/* Public Pricing Section on Landing */}
            <div style={{ width: '100%', marginTop: 8 }}>
              <PricingSection
                domain="twoja-domena.pl"
                currentPlan="free"
                usedCount={0}
                onPlanUpdated={() => {
                  handleGoogleLogin();
                }}
                onOpenContactModal={handleOpenContactModal}
              />
            </div>

            {/* FAQ Section (Matching FAQPage Schema.org) */}
            <section
              aria-labelledby="faq-heading"
              style={{
                width: '100%',
                marginTop: 8,
                display: 'flex',
                flexDirection: 'column',
                gap: 18,
              }}
            >
              <div style={{ textAlign: 'center', marginBottom: 4 }}>
                <span className="micro-tag micro-tag-info" style={{ marginBottom: 6 }}>
                  Baza Wiedzy
                </span>
                <h2
                  id="faq-heading"
                  style={{
                    fontSize: 22,
                    fontWeight: 700,
                    color: '#0B192C',
                    fontFamily: 'var(--font-display)',
                    margin: '4px 0',
                  }}
                >
                  Najczęściej zadawane pytania (FAQ)
                </h2>
                <p style={{ fontSize: 13, color: '#64748b', margin: 0 }}>
                  Wszystko, co musisz wiedzieć o bezpieczeństwie i kompatybilności CerebroSign Workspace Edition.
                </p>
              </div>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
                  gap: 18,
                }}
              >
                <article
                  className="card card-enterprise"
                  style={{
                    padding: 22,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <ShieldCheck size={18} color="#059669" />
                    <span className="micro-tag micro-tag-success">Prywatność</span>
                  </div>
                  <h3 style={{ fontSize: 15.5, fontWeight: 700, color: '#0B192C', lineHeight: 1.4, margin: 0, fontFamily: 'var(--font-display)' }}>
                    Czy CerebroSign ma wgląd w treść wysyłanych wiadomości e-mail?
                  </h3>
                  <p style={{ fontSize: 13, color: '#475569', lineHeight: 1.6, margin: 0 }}>
                    Nie. CerebroSign ma dostęp wyłącznie do pola stopki w ustawieniach konta. Aplikacja nie posiada uprawnień do czytania Twojej skrzynki odbiorczej, treści wiadomości, historii korespondencji ani załączników e-mail. Modyfikowane jest wyłącznie pole podpisu.
                  </p>
                </article>

                <article
                  className="card card-enterprise"
                  style={{
                    padding: 22,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <FileCheck size={18} color="#0066FF" />
                    <span className="micro-tag micro-tag-info">Zgodność HTML</span>
                  </div>
                  <h3 style={{ fontSize: 15.5, fontWeight: 700, color: '#0B192C', lineHeight: 1.4, margin: 0, fontFamily: 'var(--font-display)' }}>
                    Czy szablony podpisów wyświetlają się poprawnie w programie Microsoft Outlook?
                  </h3>
                  <p style={{ fontSize: 13, color: '#475569', lineHeight: 1.6, margin: 0 }}>
                    Tak. Szablony CerebroSign są kodowane na bazie rygorystycznego, tabelarycznego kodu HTML bez zewnętrznych arkuszy stylów CSS, co zapewnia ich bezbłędne renderowanie w Outlooku, Apple Mail oraz na smartfonach z systemem iOS i Android.
                  </p>
                </article>
              </div>
            </section>
          </div>
        ) : (
          /* Main Authenticated Dashboard with Tabbed Navigation */
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* Top Navigation Tab Bar */}
            <div className="nav-tabs-bar">
              <button
                className={`nav-tab-item ${activeTab === 'editor' ? 'active' : ''}`}
                onClick={() => setActiveTab('editor')}
              >
                <Layout size={16} />
                <span>Edytor stopki</span>
              </button>

              <button
                className={`nav-tab-item ${activeTab === 'users' ? 'active' : ''}`}
                onClick={() => setActiveTab('users')}
              >
                <Users size={16} />
                <span>Użytkownicy domeny</span>
                <span
                  style={{
                    background: activeTab === 'users' ? 'rgba(255, 255, 255, 0.25)' : '#e2e8f0',
                    fontSize: 11,
                    padding: '1px 7px',
                    borderRadius: 999,
                    color: activeTab === 'users' ? '#fff' : '#475569',
                  }}
                >
                  {users.length}
                </span>
              </button>

              <button
                className={`nav-tab-item ${activeTab === 'groups' ? 'active' : ''}`}
                onClick={() => setActiveTab('groups')}
              >
                <Layers size={16} />
                <span>Grupy i szablony</span>
                <span
                  style={{
                    background: activeTab === 'groups' ? 'rgba(255, 255, 255, 0.25)' : '#e2e8f0',
                    fontSize: 11,
                    padding: '1px 7px',
                    borderRadius: 999,
                    color: activeTab === 'groups' ? '#fff' : '#475569',
                  }}
                >
                  {userGroups.length}
                </span>
              </button>

              <button
                className={`nav-tab-item ${activeTab === 'pricing' ? 'active' : ''}`}
                onClick={() => setActiveTab('pricing')}
              >
                <CreditCard size={16} />
                <span>Cennik / Pakiet</span>
                {currentPlan === 'free' && (
                  <span
                    style={{
                      background: 'rgba(245, 158, 11, 0.2)',
                      color: '#f59e0b',
                      fontSize: 10,
                      fontWeight: 700,
                      padding: '1px 6px',
                      borderRadius: 4,
                      textTransform: 'uppercase',
                    }}
                  >
                    Solo
                  </span>
                )}
              </button>

              <button
                className={`nav-tab-item ${activeTab === 'settings' ? 'active' : ''}`}
                onClick={() => setActiveTab('settings')}
              >
                <Settings size={16} />
                <span>Ustawienia</span>
              </button>
            </div>

            {/* TAB CONTENT */}
            {activeTab === 'editor' && (
              <SignatureEditor
                activeUser={currentPreviewUser}
                domain={domain}
                currentPlan={currentPlan}
                googleAccessToken={googleAccessToken}
                savedTemplates={savedTemplates}
                groups={userGroups}
                onSignatureInjected={() => {
                  loadUsers();
                }}
                onSaveTemplate={handleSaveTemplate}
                onApplyTemplateToGroup={handleApplyTemplateToGroup}
                onApplyTemplateToUser={handleApplyTemplateToUser}
                onApplyTemplateToAll={handleApplyTemplateToAll}
                onUpdateUserAvatar={(userId, avatarUrl) => {
                  setUsers((prev) =>
                    prev.map((u) => (u.id === userId ? { ...u, avatarUrl } : u))
                  );
                }}
              />
            )}

            {activeTab === 'users' && (
              <>
                <StatsBar users={users} isLiveConnected={isLiveConnected} />
                <div className="dashboard-grid">
                  <UserTable
                    users={users}
                    selectedUserIds={selectedUserIds}
                    activePreviewUser={currentPreviewUser}
                    groups={userGroups}
                    savedTemplates={savedTemplates}
                    domain={domain}
                    companyPhoneDefault={savedTemplates[0]?.companyPhone || '+48 22 123 45 67'}
                    companyWebsiteDefault={savedTemplates[0]?.websiteUrl || `https://${domain}`}
                    companyAddressDefault={savedTemplates[0]?.address || 'ul. Prosta 20, 00-850 Warszawa'}
                    onToggleSelectUser={handleToggleSelectUser}
                    onToggleSelectAll={handleToggleSelectAll}
                    onSelectPreviewUser={(u) => setActivePreviewUser(u)}
                    onTriggerBatchInjection={handleTriggerBatchInjection}
                    onRefreshUsers={loadUsers}
                    onAssignGroup={handleAssignGroup}
                    onAssignSignature={handleAssignSignature}
                    onUserUpdated={(updatedUser) => {
                      setUsers((prev) =>
                        prev.map((u) =>
                          u.id === updatedUser.id || (u.primaryEmail && u.primaryEmail === updatedUser.primaryEmail)
                            ? updatedUser
                            : u
                        )
                      );
                      if (
                        activePreviewUser?.id === updatedUser.id ||
                        (activePreviewUser?.primaryEmail && activePreviewUser?.primaryEmail === updatedUser.primaryEmail)
                      ) {
                        setActivePreviewUser(updatedUser);
                      }
                    }}
                    isLoading={isLoading}
                  />
                  <div className="sticky-dashboard-col" style={{ position: 'sticky', top: 88 }}>
                    <SignaturePreview
                      activeUser={currentPreviewUser}
                      savedTemplates={savedTemplates}
                      groups={userGroups}
                      onInjectSingle={handleInjectSingle}
                      isInjecting={isInjectingSingle}
                    />
                  </div>
                </div>
              </>
            )}

            {activeTab === 'groups' && (
              <GroupsManager
                domain={domain}
                groups={userGroups}
                users={users}
                savedTemplates={savedTemplates}
                googleAccessToken={googleAccessToken}
                onGroupsUpdated={(updated) => setUserGroups(updated)}
                onUsersUpdated={(updated) => setUsers(updated)}
                onNavigateToEditorWithTemplate={(_tplId) => {
                  setActiveTab('editor');
                }}
              />
            )}

            {activeTab === 'pricing' && (
              <PricingSection
                domain={domain}
                currentPlan={currentPlan}
                usedCount={users.length}
                onPlanUpdated={(newPlan) => {
                  setCurrentPlan(newPlan);
                }}
                onOpenContactModal={handleOpenContactModal}
              />
            )}

            {activeTab === 'settings' && (
              <SettingsTab
                domain={domain}
                authUser={authUser}
                currentPlan={currentPlan}
                onOpenSecurityModal={() => setIsSecurityModalOpen(true)}
              />
            )}
          </div>
        )}
      </main>

      {/* Footer - Enterprise B2B Clean */}
      <footer
        style={{
          borderTop: '1px solid rgba(255, 255, 255, 0.08)',
          padding: '24px 24px',
          fontSize: 12.5,
          color: '#94a3b8',
          background: '#0B192C',
        }}
      >
        <div
          style={{
            maxWidth: 1400,
            margin: '0 auto',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 14,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span>&copy; 2026 <strong>CerebroSign Workspace Edition</strong></span>
            <span>&bull;</span>
            <span>Produkt ekosystemu</span>
            <a
              href="https://cerebrolabs.pl"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#60a5fa', textDecoration: 'none', fontWeight: 600 }}
            >
              CerebroLabs.pl
            </a>
          </div>

          <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#60a5fa' }}>
              <ShieldCheck size={14} />
              Google Workspace API
            </span>
            <span>&bull;</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#34d399' }}>
              <FileCheck size={14} />
              Zgodność z RODO / GDPR
            </span>
            <span>&bull;</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: '#cbd5e1' }}>
              <Lock size={14} />
              Szyfrowanie TLS 1.3 / SSL
            </span>
            <span>&bull;</span>
            <button
              onClick={() => handleOpenContactModal('general')}
              style={{ background: 'none', border: 'none', color: '#60a5fa', cursor: 'pointer', padding: 0, fontSize: 12.5, fontWeight: 500 }}
            >
              Kontakt B2B
            </button>
          </div>
        </div>
      </footer>

      {/* Modals */}
      <BatchModal
        isOpen={isBatchModalOpen}
        onClose={() => setIsBatchModalOpen(false)}
        selectedUsers={users.filter((u) => selectedUserIds.has(u.id))}
        progress={batchProgress}
        isProcessing={isBatchProcessing}
        result={batchResult}
      />

      <SecurityModal
        isOpen={isSecurityModalOpen}
        onClose={() => setIsSecurityModalOpen(false)}
      />

      <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        preselectedPlan={contactModalPlan}
        domain={domain}
        defaultEmail={authUser?.email}
        defaultName={authUser?.displayName}
      />
    </div>
  );
};
export default App;
